import { getHierarchyTlfType } from "./getHierarchyTlfType";
import { HierarchyContextProps, IHierarchyActionLoadClinicalLibrary } from "./interface";
import { mergeClinicalLibraryData } from "./mergeClinicalLibraryData";

/**
 * Reducer function to load clinical library data for a specific hierarchy.
 * Updates the hierarchy node with the clinical library and rebuilds parent templates.
 *
 * @param action - Action containing the hierarchy ID and clinical library data
 * @param state - Current hierarchy context state
 * @returns Updated hierarchy context state
 */
export const hcr_loadClinicalLibrary = (
  action: IHierarchyActionLoadClinicalLibrary,
  state: HierarchyContextProps,
): HierarchyContextProps => {
  if (!action.clinicalLibrary || action.clinicalLibrary.length === 0) {
    return state;
  }

  // Find the hierarchy nodes to update
  const updatedHierarchyNodes = state.hierarchyNodes.map((hn) => {
    const matchingLibrary = action.clinicalLibrary.find((cl) => cl.hierarchyId === hn.id);
    if (matchingLibrary) {
      return {
        ...hn,
        clinicalLibrary: matchingLibrary,
      };
    }
    return hn;
  });

  // If current hierarchy is being updated, update it as well
  const matchingHierarchyLibrary = state.hierarchy
    ? action.clinicalLibrary.find((cl) => cl.hierarchyId === state.hierarchy?.id)
    : undefined;

  const updatedHierarchy =
    state.hierarchy && matchingHierarchyLibrary
      ? {
          ...state.hierarchy,
          clinicalLibrary: matchingHierarchyLibrary,
        }
      : state.hierarchy;

  if (!state.hierarchy) {
    // If no current hierarchy, just update the nodes. `updatedHierarchy` is guaranteed to equal
    // state.hierarchy (falsy) here since the ternary above only produces a new object when
    // state.hierarchy is truthy, so there is nothing to assign for the hierarchy field.
    return {
      ...state,
      hierarchyNodes: updatedHierarchyNodes,
    };
  }

  // Rebuild parent templates by iterating through the clinical library collection
  // Filter to only include libraries from hierarchies with matching TLF type. Study-level
  // hierarchies have no Shell-level ancestors so this naturally excludes any merge for them,
  // while Report-level hierarchies still merge in clinical library data from their Study parent.
  const thisTlfType = getHierarchyTlfType(state.hierarchy.level);
  const relevantLibraries = action.clinicalLibrary.filter(
    (cl) =>
      cl.hierarchyId !== state.hierarchy!.id &&
      getHierarchyTlfType(cl.hierarchyLevel) === thisTlfType &&
      updatedHierarchyNodes.some((hn) => hn.id === cl.hierarchyId),
  );

  // Merge clinical library data into parent templates using reduce pattern from hcr_loadHierarchy
  const clinicalTemplates = relevantLibraries.reduce(
    (acc, clinicalLibrary) => mergeClinicalLibraryData(acc, clinicalLibrary),
    {
      clinicalDatasets: [...state.parentTemplates.clinicalDatasets],
      clinicalCodeLists: [...state.parentTemplates.clinicalCodeLists],
      clinicalValueLists: [...state.parentTemplates.clinicalValueLists],
      clinicalComments: [...state.parentTemplates.clinicalComments],
      clinicalMethods: [...state.parentTemplates.clinicalMethods],
      clinicalWhereClauses: [...state.parentTemplates.clinicalWhereClauses],
    },
  );

  const newParentTemplates = {
    ...clinicalTemplates,
    domainReplacements: [...state.parentTemplates.domainReplacements],
    lists: [...state.parentTemplates.lists],
    rowGroups: [...state.parentTemplates.rowGroups],
    singles: [...state.parentTemplates.singles],
    analysisSets: [...state.parentTemplates.analysisSets],
    analysisMethods: [...state.parentTemplates.analysisMethods],
    dataSubsets: [...state.parentTemplates.dataSubsets],
    groupingFactors: [...state.parentTemplates.groupingFactors],
  };

  return {
    ...state,
    hierarchyNodes: updatedHierarchyNodes,
    hierarchy: updatedHierarchy,
    parentTemplates: newParentTemplates,
  };
};
